import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, DollarSign, Users, Target, Award, Zap, Download } from "lucide-react";
import { toast } from "sonner";
import { exportToCSV } from "@/lib/export-utils";

interface CommissionTier {
  id: string;
  name: string;
  min_referrals: number;
  max_referrals: number | null;
  base_rate: number;
  bonus_rate: number;
  milestone_bonus: number;
  description: string | null;
  color: string;
  icon: string;
  tier_order: number;
}

interface AffiliateMetrics {
  total_clicks: number;
  total_conversions: number;
  conversion_rate: number;
  total_revenue: number;
  total_commission: number;
  team_size: number;
  team_revenue: number;
  lifetime_value: number;
  performance_score: number;
  rank: number | null;
  badges: any;
}

interface Props {
  activeReferrals: number;
  totalEarnings: number;
  pendingEarnings: number;
}

export const PerformanceDashboard = ({ activeReferrals, totalEarnings, pendingEarnings }: Props) => {
  const [tiers, setTiers] = useState<CommissionTier[]>([]);
  const [currentTier, setCurrentTier] = useState<CommissionTier | null>(null);
  const [nextTier, setNextTier] = useState<CommissionTier | null>(null);
  const [metrics, setMetrics] = useState<AffiliateMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [activeReferrals]);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load commission tiers
      const { data: tiersData } = await supabase
        .from("commission_tiers")
        .select("*")
        .eq("is_active", true)
        .order("tier_order", { ascending: true });

      if (tiersData) {
        setTiers(tiersData);
        
        // Find current tier
        const current = tiersData.find(
          (tier) =>
            activeReferrals >= tier.min_referrals &&
            (tier.max_referrals === null || activeReferrals <= tier.max_referrals)
        );
        setCurrentTier(current || tiersData[0]);

        // Find next tier
        if (current) {
          const next = tiersData.find((tier) => tier.tier_order === current.tier_order + 1);
          setNextTier(next || null);
        }
      }

      // Load metrics
      const { data: metricsData } = await supabase
        .from("affiliate_metrics")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (metricsData) {
        setMetrics(metricsData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadMetrics = () => {
    if (!metrics) return;
    
    const exportData = [
      { metric: 'Total Clicks', value: metrics.total_clicks },
      { metric: 'Conversions', value: metrics.total_conversions },
      { metric: 'Conversion Rate', value: `${(metrics.conversion_rate * 100).toFixed(2)}%` },
      { metric: 'Total Revenue', value: `$${(metrics.total_revenue / 100).toFixed(2)}` },
      { metric: 'Total Commission', value: `$${(metrics.total_commission / 100).toFixed(2)}` },
      { metric: 'Team Size', value: metrics.team_size },
      { metric: 'Team Revenue', value: `$${(metrics.team_revenue / 100).toFixed(2)}` },
      { metric: 'Performance Score', value: metrics.performance_score },
      { metric: 'Current Rank', value: metrics.rank || 'Unranked' }
    ];

    const filename = `affiliate_metrics_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(exportData, filename);
    
    toast.success("Metrics downloaded successfully");
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  const getTierIcon = (icon: string) => {
    const icons: Record<string, JSX.Element> = {
      award: <Award className="w-5 h-5" />,
      medal: <Target className="w-5 h-5" />,
      trophy: <Award className="w-5 h-5" />,
      crown: <Zap className="w-5 h-5" />,
      gem: <Zap className="w-5 h-5" />,
    };
    return icons[icon] || <Award className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Current Tier Card */}
      {currentTier && (
        <Card className="border-2" style={{ borderColor: currentTier.color }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${currentTier.color}20` }}>
                  {getTierIcon(currentTier.icon)}
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {currentTier.name} Tier
                    <Badge style={{ backgroundColor: currentTier.color }}>Active</Badge>
                  </CardTitle>
                  <CardDescription>{currentTier.description}</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Base Commission</p>
                <p className="text-2xl font-bold">
                  {(currentTier.base_rate * 100).toFixed(0)}%
                </p>
              </div>
              {currentTier.bonus_rate > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Bonus Rate</p>
                  <p className="text-2xl font-bold text-green-600">
                    +{(currentTier.bonus_rate * 100).toFixed(0)}%
                  </p>
                </div>
              )}
              {currentTier.milestone_bonus > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Milestone Bonus</p>
                  <p className="text-2xl font-bold">
                    ${(currentTier.milestone_bonus / 100).toFixed(0)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Total Rate</p>
                <p className="text-2xl font-bold text-primary">
                  {((currentTier.base_rate + currentTier.bonus_rate) * 100).toFixed(0)}%
                </p>
              </div>
            </div>

            {nextTier && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Progress to {nextTier.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {activeReferrals} / {nextTier.min_referrals} referrals
                  </p>
                </div>
                <div className="w-full bg-background rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min((activeReferrals / nextTier.min_referrals) * 100, 100)}%`,
                      backgroundColor: nextTier.color,
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {nextTier.min_referrals - activeReferrals} more referrals to unlock{" "}
                  {((nextTier.base_rate + nextTier.bonus_rate) * 100).toFixed(0)}% commission rate
                  {nextTier.milestone_bonus > 0 && (
                    <> and ${(nextTier.milestone_bonus / 100).toFixed(0)} bonus</>
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Your detailed affiliate performance statistics</CardDescription>
            </div>
            <Button onClick={handleDownloadMetrics} variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Conversion Rate</p>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">
                {metrics?.conversion_rate ? (metrics.conversion_rate * 100).toFixed(1) : 0}%
              </div>
              <p className="text-xs text-muted-foreground">
                {metrics?.total_conversions || 0} conversions from {metrics?.total_clicks || 0} clicks
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Lifetime Value</p>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">
                ${((metrics?.lifetime_value || 0) / 100).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Total value generated
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Team Size</p>
                <Users className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">{metrics?.team_size || 0}</div>
              <p className="text-xs text-muted-foreground">
                Team revenue: ${((metrics?.team_revenue || 0) / 100).toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* All Tiers Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Commission Tier Structure</CardTitle>
          <CardDescription>Earn more as you grow your referrals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tiers.map((tier) => {
              const isActive = currentTier?.id === tier.id;
              return (
                <div
                  key={tier.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isActive ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${tier.color}20` }}
                      >
                        {getTierIcon(tier.icon)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{tier.name}</h4>
                          {isActive && <Badge>Current</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {tier.min_referrals} - {tier.max_referrals || "∞"} referrals
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">
                        {((tier.base_rate + tier.bonus_rate) * 100).toFixed(0)}%
                      </p>
                      {tier.milestone_bonus > 0 && (
                        <p className="text-sm text-green-600">
                          +${(tier.milestone_bonus / 100).toFixed(0)} bonus
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
